import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { checkSubscriptionStatus } from '@/services/stripe';
import { useSearchParams } from 'react-router-dom';

export type SubscriptionPlan = 
  | 'free' | 'pro'  // Customer plans
  | 'studio_free' | 'studio_pro' | 'studio_business' | 'studio_premium';  // Studio plans

export interface UserSubscription {
  plan: SubscriptionPlan;
  quotaAppointments: number | null;
  quotaStaff: number | null;
  currentPeriodEnd: string | null;
  isActive: boolean;
  stripeCustomerId?: string;
}

export interface SubscriptionFeature {
  id: string;
  plan: SubscriptionPlan;
  monthlyPrice: number;
  isCustomerPlan: boolean;
  hasEarlyAccess: boolean;
  hasReferralBonus: boolean;
  hasCalendarExport: boolean;
  hasAddons: boolean;
  hasAnalytics: boolean;
}

export const useSubscription = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [subscriptionFeatures, setSubscriptionFeatures] = useState<SubscriptionFeature[]>([]);

  // Check if there's a session_id in the URL query params indicating a successful checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      toast({
        title: "Abonnement erfolgreich",
        description: "Dein Abonnement wurde erfolgreich aktualisiert",
      });
      
      // Clear the session_id from the URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Force refresh subscription data
      fetchUserSubscription();
    }
    
    const canceled = searchParams.get('canceled');
    if (canceled) {
      toast({
        title: "Checkout abgebrochen",
        description: "Der Checkout-Vorgang wurde abgebrochen",
        variant: "destructive",
      });
      
      // Clear the canceled param from the URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [searchParams]);

  const fetchUserSubscription = async () => {
    try {
      setLoading(true);
      
      if (!user) {
        setSubscription(null);
        return;
      }

      // First, try to get the latest from Stripe via Edge Function
      try {
        const stripeData = await checkSubscriptionStatus();
        console.log("Stripe subscription data:", stripeData);
      } catch (error) {
        console.error("Error fetching from Stripe:", error);
        // Continue with Supabase data if Stripe fails
      }

      // Get the subscription data from Supabase
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id as any)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      if (error) {
        // If no subscription found, simulate a default "free" plan
        if (error.code === 'PGRST116') {
          // Get user role to determine default plan
          const { data: profileData } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id as any)
            .single();
          
          const roleValue = profileData && typeof profileData === 'object' && 'role' in profileData 
            ? profileData.role 
            : 'customer';
            
          // Fix: Cast the role string to a string type first for comparison
          // This allows comparing the string value without TypeScript errors
          const isBusinessAccount = String(roleValue) === 'unternehmen';
          const defaultPlan = isBusinessAccount ? 'studio_free' as SubscriptionPlan : 'free' as SubscriptionPlan;
          
          setSubscription({
            plan: defaultPlan,
            quotaAppointments: null, // Unlimited
            quotaStaff: null,
            currentPeriodEnd: null,
            isActive: true
          });
          return;
        }
        throw error;
      }
      
      if (data) {
        const planValue = typeof data === 'object' && 'plan' in data ? data.plan : 'free';
        const quotaAppointmentsValue = typeof data === 'object' && 'quota_appointments' in data ? data.quota_appointments : null;
        const quotaStaffValue = typeof data === 'object' && 'quota_staff' in data ? data.quota_staff : null;
        const currentPeriodEndValue = typeof data === 'object' && 'current_period_end' in data ? data.current_period_end : null;
        const stripeCustomerIdValue = typeof data === 'object' && 'stripe_customer_id' in data ? data.stripe_customer_id : undefined;
        
        // Handle the case where an old 'studio_basic' plan might exist in the DB
        let normalizedPlan = planValue;
        if (normalizedPlan === 'studio_basic') {
          normalizedPlan = 'studio_pro';
        }
        
        setSubscription({
          plan: normalizedPlan as SubscriptionPlan,
          quotaAppointments: quotaAppointmentsValue,
          quotaStaff: quotaStaffValue,
          currentPeriodEnd: currentPeriodEndValue,
          isActive: currentPeriodEndValue ? new Date(currentPeriodEndValue) > new Date() : true,
          stripeCustomerId: stripeCustomerIdValue
        });
      }
    } catch (error: any) {
      console.error('Error fetching subscription:', error);
      toast({
        title: 'Fehler',
        description: `Fehler beim Laden der Abonnementdaten: ${error.message}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionFeatures = async () => {
    try {
      // Updated subscription features according to the matrix in requirements
      setSubscriptionFeatures([
        {
          id: 'free',
          plan: 'free',
          monthlyPrice: 0,
          isCustomerPlan: true,
          hasEarlyAccess: false,
          hasReferralBonus: false,
          hasCalendarExport: false,
          hasAddons: false,
          hasAnalytics: false
        },
        {
          id: 'pro',
          plan: 'pro',
          monthlyPrice: 1,
          isCustomerPlan: true,
          hasEarlyAccess: true,
          hasReferralBonus: true,
          hasCalendarExport: true,
          hasAddons: true,
          hasAnalytics: false
        },
        {
          id: 'studio_free',
          plan: 'studio_free',
          monthlyPrice: 0,
          isCustomerPlan: false,
          hasEarlyAccess: false,
          hasReferralBonus: false,
          hasCalendarExport: false,
          hasAddons: false,
          hasAnalytics: false
        },
        {
          id: 'studio_pro',
          plan: 'studio_pro',
          monthlyPrice: 19,
          isCustomerPlan: false,
          hasEarlyAccess: false,
          hasReferralBonus: true,
          hasCalendarExport: true,
          hasAddons: false,
          hasAnalytics: true
        },
        {
          id: 'studio_business',
          plan: 'studio_business',
          monthlyPrice: 39,
          isCustomerPlan: false,
          hasEarlyAccess: true,
          hasReferralBonus: true,
          hasCalendarExport: true,
          hasAddons: true,
          hasAnalytics: true
        },
        {
          id: 'studio_premium',
          plan: 'studio_premium',
          monthlyPrice: 59,
          isCustomerPlan: false,
          hasEarlyAccess: true,
          hasReferralBonus: true,
          hasCalendarExport: true,
          hasAddons: true,
          hasAnalytics: true
        }
      ]);
    } catch (error: any) {
      console.error('Error fetching subscription features:', error);
    }
  };

  const checkFeatureAccess = async (featureName: string): Promise<boolean> => {
    // Mock implementation until the actual Edge Function is available
    try {
      if (!subscription) return false;
      
      // Simple logic based on plan type
      if (subscription.plan === 'free') {
        return ['basic_booking'].includes(featureName);
      } else if (subscription.plan === 'pro') {
        return ['basic_booking', 'calendar_export', 'booking_history', 'early_access'].includes(featureName);
      } else if (subscription.plan.startsWith('studio_')) {
        // Studio plans have different features
        if (subscription.plan === 'studio_free') {
          return ['staff_management', 'basic_booking'].includes(featureName);
        } else if (subscription.plan === 'studio_pro') {
          return ['staff_management', 'basic_booking', 'calendar_export', 'analytics'].includes(featureName);
        } else {
          return true; // Paid studio plans have all features
        }
      }
      
      return false;
    } catch (error: any) {
      console.error(`Error checking feature access for ${featureName}:`, error);
      return false;
    }
  };

  const canBookAppointment = async (): Promise<boolean> => {
    try {
      if (!subscription) return false;
      
      // If quota is null, it means unlimited
      if (subscription.quotaAppointments === null) {
        return true;
      }
      
      // In a real implementation, you'd check against actual booking count
      // For now, assume the user is under quota
      return true;
    } catch (error: any) {
      console.error('Error checking booking permission:', error);
      return false;
    }
  };

  useEffect(() => {
    if (user) {
      fetchUserSubscription();
      fetchSubscriptionFeatures();
    } else {
      setSubscription(null);
    }
  }, [user]);

  return {
    loading,
    subscription,
    subscriptionFeatures,
    fetchUserSubscription,
    checkFeatureAccess,
    canBookAppointment,
    getCustomerPlans: () => subscriptionFeatures.filter(f => f.isCustomerPlan),
    getStudioPlans: () => subscriptionFeatures.filter(f => !f.isCustomerPlan),
  };
};
